/**
 * usePlayerNotifications
 *
 * Fetches, generates, and manages in-app notifications for logged-in players
 * who have a claimed + approved player profile for the current season.
 *
 * Notification types (verified against live schema):
 *  1. new_photo_album          — a new photo_albums row was created for the active season
 *  2. game_score_posted        — a game the player's team played is now status='finished'
 *  3. claim_approved           — one of the player's player_claims rows changed to 'approved'
 *  4. upcoming_game            — team has a game within `hours_before` hours
 *  5. stat_adjustment_approved — a stat_adjustment_requests row where captain_id = profile_id
 *                                changed to 'approved' (captain-only)
 *
 * Architecture:
 *  - Persisted notifications live in `player_notifications` (inserted server-side or here).
 *  - This hook reads from that table and also generates ephemeral "upcoming game" alerts
 *    in real-time (since those are time-based and don't need persistence).
 *  - Notification settings are read from `player_notification_settings`.
 *  - The hook exposes markRead() and markAllRead() for the UI.
 *  - Every notification type that fires an in-app alert also fires a mobile push
 *    notification via the push-notifications Edge Function (sendToProfile action),
 *    subject to the same user preference toggle that controls the in-app version.
 *
 * Push notification behaviour:
 *  - Persisted types (new_photo_album, game_score_posted, claim_approved,
 *    stat_adjustment_approved): push is fired inside upsertNotification() immediately
 *    after a new row is inserted. The dedup_key prevents duplicate pushes across
 *    sessions because upsertNotification() returns early if the row already exists.
 *  - Ephemeral type (upcoming_game): push is fired inside
 *    buildUpcomingGameNotification() using a localStorage dedup key so it fires
 *    at most once per game per device, respecting the upcoming_game setting.
 *
 * Schema references (confirmed from schema_dump.sql):
 *  - player_notifications: id, profile_id, type, title, body, action_url, metadata, is_read, created_at
 *  - player_notification_settings: profile_id, new_photo_album, game_score_posted,
 *      claim_approved, upcoming_game, stat_adjustment_approved, upcoming_game_hours_before
 *  - player_claims: id, profile_id, player_id, status (claim_status enum), requested_at
 *  - stat_adjustment_requests: id, game_id, team_id, captain_id (uuid), status, created_at
 *  - photo_albums: id, season_id, week_number, folder_id, label, synced_at, created_at
 *  - games: id, status, game_date, season_id, team1_id, team2_id
 *  - players: id, profile_id, team_id, season_id
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";

/**
 * Send a native OS push notification to the user's device (if they have
 * the PWA installed and push subscribed). Fire-and-forget — never throws.
 *
 * @param {string} profileId  - UUID of the target user
 * @param {object} payload    - { title, body, url }
 */
async function sendPushToProfile(profileId, { title, body, url }) {
  if (!profileId) return;
  try {
    await supabase.functions.invoke("push-notifications", {
      body: {
        action: "sendToProfile",
        profile_id: profileId,
        title,
        body,
        url: url ?? "/player-dashboard",
        icon: "/logo512.png",
        badge: "/logo192.png",
      },
    });
  } catch (err) {
    // Non-critical — in-app notification already inserted
    console.warn("[usePlayerNotifications] sendPushToProfile failed:", err);
  }
}

// Default settings (all enabled, 24h before game)
const DEFAULT_SETTINGS = {
  new_photo_album: true,
  game_score_posted: true,
  claim_approved: true,
  upcoming_game: true,
  stat_adjustment_approved: true,
  upcoming_game_hours_before: 24,
};

/**
 * localStorage key for tracking which upcoming-game push notifications have
 * already been sent on this device, preventing re-fires on every page load.
 * Format: "mh_push_sent_upcoming_<gameId>"
 */
function upcomingGamePushKey(gameId) {
  return `mh_push_sent_upcoming_${gameId}`;
}

/**
 * @param {object} params
 * @param {object|null} params.user          — from useAuth()
 * @param {object|null} params.activeSeason  — from useSeason()
 * @param {boolean}     params.isCaptain     — from useAuth()
 * @param {boolean}     [params.isAdmin]     — from useAuth(); skips player-only seeding
 * @param {number|null} params.resolvedTeamId — player's team for this season
 */
export function usePlayerNotifications({
  user,
  activeSeason,
  isCaptain,
  isAdmin = false,
  resolvedTeamId,
}) {
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const profileId = user?.id ?? null;
  const seasonId = activeSeason?.id ?? null;

  // Track whether we've already seeded notifications for this session
  // to avoid duplicate inserts on re-renders.
  const seededRef = useRef(false);

  // ── Load notification settings ──────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    if (!profileId) return;
    setSettingsLoading(true);
    const { data, error } = await supabase
      .from("player_notification_settings")
      .select("*")
      .eq("profile_id", profileId)
      .maybeSingle();

    if (!error && data) {
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    }
    // If no row exists yet, keep defaults — row will be created on first save.
    setSettingsLoading(false);
  }, [profileId]);

  // ── Load persisted notifications from DB ────────────────────────────────────
  const loadPersistedNotifications = useCallback(async () => {
    if (!profileId) return [];
    const { data, error } = await supabase
      .from("player_notifications")
      .select("*")
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("[usePlayerNotifications] load error:", error);
      return [];
    }
    return data ?? [];
  }, [profileId]);

  // ── Insert a notification if it doesn't already exist ──────────────────────
  // Uses metadata.dedup_key to prevent duplicates across sessions.
  // When a genuinely new notification is inserted, also fires a native OS push
  // via the push-notifications Edge Function (sendToProfile action).
  // The push is gated by the same dedup check — if the row already exists,
  // neither the in-app insert nor the push fires.
  const upsertNotification = useCallback(
    async ({ type, title, body, action_url, metadata = {} }) => {
      if (!profileId) return;

      // Check if a non-read notification of this type+dedup_key already exists
      const dedupKey = metadata.dedup_key;
      if (dedupKey) {
        const { data: existing } = await supabase
          .from("player_notifications")
          .select("id")
          .eq("profile_id", profileId)
          .eq("type", type)
          .contains("metadata", { dedup_key: dedupKey })
          .maybeSingle();
        if (existing) return; // already exists — skip insert AND push
      }

      const { error } = await supabase.from("player_notifications").insert({
        profile_id: profileId,
        type,
        title,
        body,
        action_url,
        metadata,
      });

      // If insert succeeded, fire a native push notification to the user's device.
      // The push respects the same user preference that gated the seed function
      // calling upsertNotification (e.g. settings.new_photo_album was already
      // checked before seedPhotoAlbumNotifications called us).
      if (!error) {
        sendPushToProfile(profileId, { title, body, url: action_url });
      }
    },
    [profileId],
  );

  // ── Seed: new photo albums ──────────────────────────────────────────────────
  const seedPhotoAlbumNotifications = useCallback(async () => {
    // Skip for admin-only users — their notifications come from DB triggers.
    if (isAdmin || !settings.new_photo_album || !seasonId || !profileId) return;

    // Find albums created in the last 7 days for the active season
    const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
    const { data: albums, error } = await supabase
      .from("photo_albums")
      .select("id, label, week_number, created_at")
      .eq("season_id", seasonId)
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5);

    if (error || !albums?.length) return;

    for (const album of albums) {
      const label = album.label || `Week ${album.week_number}`;
      await upsertNotification({
        type: "new_photo_album",
        title: "New Photo Album Posted",
        body: `Check out the new photos from ${label}!`,
        action_url: `/gallery/week/${album.week_number}`,
        metadata: { dedup_key: `album_${album.id}`, album_id: album.id },
      });
    }
  }, [
    settings.new_photo_album,
    seasonId,
    profileId,
    upsertNotification,
    isAdmin,
  ]);

  // ── Seed: game score posted (team's finished games) ─────────────────────────
  const seedGameScoreNotifications = useCallback(async () => {
    if (
      !settings.game_score_posted ||
      !seasonId ||
      !resolvedTeamId ||
      !profileId
    )
      return;

    // Find games for this team that finished in the last 7 days
    const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
    const { data: games, error } = await supabase
      .from("games")
      .select(
        `id, game_date, team1_score, team2_score, status,
         team1:teams!games_team1_id_fkey(id, name),
         team2:teams!games_team2_id_fkey(id, name)`,
      )
      .eq("season_id", seasonId)
      .eq("status", "finished")
      .or(`team1_id.eq.${resolvedTeamId},team2_id.eq.${resolvedTeamId}`)
      .gte("updated_at", since)
      .order("game_date", { ascending: false })
      .limit(5);

    if (error || !games?.length) return;

    for (const game of games) {
      const isTeam1 = game.team1?.id === resolvedTeamId;
      const myTeamName = isTeam1 ? game.team1?.name : game.team2?.name;
      const oppName = isTeam1 ? game.team2?.name : game.team1?.name;
      const myScore = isTeam1 ? game.team1_score : game.team2_score;
      const oppScore = isTeam1 ? game.team2_score : game.team1_score;
      const result = myScore > oppScore ? "W" : myScore < oppScore ? "L" : "T";

      await upsertNotification({
        type: "game_score_posted",
        title: "Game Score Posted",
        body: `${myTeamName} ${result} vs ${oppName || "TBD"} — ${myScore}–${oppScore}`,
        action_url: `/game/${game.id}`,
        metadata: { dedup_key: `game_score_${game.id}`, game_id: game.id },
      });
    }
  }, [
    settings.game_score_posted,
    seasonId,
    resolvedTeamId,
    profileId,
    upsertNotification,
  ]);

  // ── Seed: claim approved ────────────────────────────────────────────────────
  const seedClaimApprovedNotifications = useCallback(async () => {
    // Skip for admin-only users — admins don't have player claims of their own.
    if (isAdmin || !settings.claim_approved || !profileId) return;

    // Find approved claims for this profile in the last 30 days
    const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
    const { data: claims, error } = await supabase
      .from("player_claims")
      .select(
        "id, reviewed_at, player:players(name, season:seasons(name), team:teams(name))",
      )
      .eq("profile_id", profileId)
      .eq("status", "approved")
      .gte("reviewed_at", since)
      .order("reviewed_at", { ascending: false })
      .limit(5);

    if (error || !claims?.length) return;

    for (const claim of claims) {
      const playerName = claim.player?.name ?? "your player profile";
      const seasonName = claim.player?.season?.name ?? "";
      const teamName = claim.player?.team?.name ?? "";

      await upsertNotification({
        type: "claim_approved",
        title: "Player Claim Approved",
        body: `Your claim for ${playerName}${seasonName ? ` (${seasonName}` : ""}${teamName ? `, ${teamName}` : ""}${seasonName ? ")" : ""} has been approved!`,
        action_url: "/player-dashboard",
        metadata: {
          dedup_key: `claim_approved_${claim.id}`,
          claim_id: claim.id,
        },
      });
    }
  }, [settings.claim_approved, profileId, upsertNotification, isAdmin]);

  // ── Seed: stat adjustment approved (captains only) ──────────────────────────
  const seedStatAdjustmentNotifications = useCallback(async () => {
    if (!settings.stat_adjustment_approved || !isCaptain || !profileId) return;

    // Find approved stat adjustment requests where this profile is the captain
    const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
    const { data: requests, error } = await supabase
      .from("stat_adjustment_requests")
      .select(
        "id, updated_at, game:games(id, game_date, team1:teams!games_team1_id_fkey(name), team2:teams!games_team2_id_fkey(name))",
      )
      .eq("captain_id", profileId)
      .eq("status", "approved")
      .gte("updated_at", since)
      .order("updated_at", { ascending: false })
      .limit(5);

    if (error || !requests?.length) return;

    for (const req of requests) {
      const t1 = req.game?.team1?.name ?? "Team 1";
      const t2 = req.game?.team2?.name ?? "Team 2";
      const dateStr = req.game?.game_date
        ? new Date(req.game.game_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })
        : "";

      await upsertNotification({
        type: "stat_adjustment_approved",
        title: "Stat Adjustment Approved",
        body: `Your stat adjustment request for ${t1} vs ${t2}${dateStr ? ` (${dateStr})` : ""} was approved.`,
        action_url: "/captain",
        metadata: {
          dedup_key: `stat_adj_approved_${req.id}`,
          request_id: req.id,
        },
      });
    }
  }, [
    settings.stat_adjustment_approved,
    isCaptain,
    profileId,
    upsertNotification,
  ]);

  // ── Generate ephemeral: upcoming game reminder ──────────────────────────────
  // This is time-based and generated fresh each load — not persisted to DB.
  // Push notification for upcoming_game is fired here (once per game per device)
  // using a localStorage dedup key, so it doesn't re-fire on every page load.
  // The push is only sent when settings.upcoming_game is enabled, mirroring the
  // in-app behaviour exactly.
  const buildUpcomingGameNotification = useCallback(async () => {
    if (!settings.upcoming_game || !seasonId || !resolvedTeamId) return null;

    const hoursWindow = settings.upcoming_game_hours_before ?? 24;
    const now = new Date();
    const windowEnd = new Date(now.getTime() + hoursWindow * 3600 * 1000);
    const today = now.toISOString().split("T")[0];
    const windowEndStr = windowEnd.toISOString().split("T")[0];

    const { data: nextGame, error } = await supabase
      .from("games")
      .select(
        `id, game_date, court, status,
         team1:teams!games_team1_id_fkey(id, name),
         team2:teams!games_team2_id_fkey(id, name)`,
      )
      .eq("season_id", seasonId)
      .neq("status", "finished")
      .or(`team1_id.eq.${resolvedTeamId},team2_id.eq.${resolvedTeamId}`)
      .gte("game_date", today)
      .lte("game_date", windowEndStr)
      .order("game_date", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error || !nextGame) return null;

    const gameDate = new Date(nextGame.game_date);
    const hoursUntil = (gameDate - now) / 3600000;
    if (hoursUntil < 0 || hoursUntil > hoursWindow) return null;

    const isTeam1 = nextGame.team1?.id === resolvedTeamId;
    const myTeamName = isTeam1 ? nextGame.team1?.name : nextGame.team2?.name;
    const oppName = isTeam1 ? nextGame.team2?.name : nextGame.team1?.name;
    const dateStr = gameDate.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    const title = "Upcoming Game";
    const body = `${myTeamName ?? "Your team"} vs ${oppName ?? "TBD"} — ${dateStr}${nextGame.court ? ` @ ${nextGame.court}` : ""}`;
    const action_url = "/schedule";

    // ── Fire push notification for upcoming_game (once per game per device) ──
    // We use localStorage to track which game IDs have already had a push sent
    // on this device. This prevents re-firing on every page load while still
    // delivering the push the first time the user opens the app within the window.
    if (profileId) {
      const pushKey = upcomingGamePushKey(nextGame.id);
      const alreadySentPush = localStorage.getItem(pushKey);
      if (!alreadySentPush) {
        // Mark as sent before the async call to prevent race conditions
        localStorage.setItem(pushKey, "1");
        // Fire-and-forget — failure is non-critical
        sendPushToProfile(profileId, { title, body, url: action_url });
      }
    }

    return {
      id: `upcoming_game_${nextGame.id}`,
      profile_id: profileId,
      type: "upcoming_game",
      title,
      body,
      action_url,
      metadata: { game_id: nextGame.id },
      is_read: false,
      created_at: new Date().toISOString(),
      ephemeral: true, // not stored in DB
    };
  }, [
    settings.upcoming_game,
    settings.upcoming_game_hours_before,
    seasonId,
    resolvedTeamId,
    profileId,
  ]);

  // ── Main fetch + seed ───────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!profileId) {
      setNotifications([]);
      return;
    }

    setLoading(true);

    // Seed new notifications (idempotent via dedup_key)
    if (!seededRef.current) {
      seededRef.current = true;
      await Promise.all([
        seedPhotoAlbumNotifications(),
        seedGameScoreNotifications(),
        seedClaimApprovedNotifications(),
        seedStatAdjustmentNotifications(),
      ]);
    }

    // Load persisted notifications
    const persisted = await loadPersistedNotifications();

    // Build ephemeral upcoming game notification (also fires push if needed)
    const upcomingNotif = await buildUpcomingGameNotification();

    // Merge: ephemeral first (most urgent), then persisted unread, then read
    const allNotifs = [...(upcomingNotif ? [upcomingNotif] : []), ...persisted];

    setNotifications(allNotifs);
    setLoading(false);
  }, [
    profileId,
    seedPhotoAlbumNotifications,
    seedGameScoreNotifications,
    seedClaimApprovedNotifications,
    seedStatAdjustmentNotifications,
    loadPersistedNotifications,
    buildUpcomingGameNotification,
  ]);

  // ── Mark a single notification as read ─────────────────────────────────────
  const markRead = useCallback(
    async (notifId) => {
      // Ephemeral notifications have no DB row
      if (typeof notifId === "string" && notifId.startsWith("upcoming_game_")) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n)),
        );
        return;
      }

      await supabase
        .from("player_notifications")
        .update({ is_read: true })
        .eq("id", notifId)
        .eq("profile_id", profileId);

      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n)),
      );
    },
    [profileId],
  );

  // ── Mark all as read ────────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    if (!profileId) return;

    await supabase
      .from("player_notifications")
      .update({ is_read: true })
      .eq("profile_id", profileId)
      .eq("is_read", false);

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [profileId]);

  // ── Delete a notification ───────────────────────────────────────────────────
  const deleteNotification = useCallback(
    async (notifId) => {
      if (typeof notifId === "string" && notifId.startsWith("upcoming_game_")) {
        setNotifications((prev) => prev.filter((n) => n.id !== notifId));
        return;
      }

      await supabase
        .from("player_notifications")
        .delete()
        .eq("id", notifId)
        .eq("profile_id", profileId);

      setNotifications((prev) => prev.filter((n) => n.id !== notifId));
    },
    [profileId],
  );

  // ── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    // Reset seed flag when user/season changes so we re-seed
    seededRef.current = false;
  }, [profileId, seasonId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ── Realtime subscription for new notifications ─────────────────────────────
  useEffect(() => {
    if (!profileId) return;

    const channel = supabase
      .channel(`player_notifications:${profileId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "player_notifications",
          filter: `profile_id=eq.${profileId}`,
        },
        (payload) => {
          setNotifications((prev) => {
            // Avoid duplicates
            if (prev.some((n) => n.id === payload.new.id)) return prev;
            return [payload.new, ...prev];
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profileId]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    settings,
    loading,
    settingsLoading,
    markRead,
    markAllRead,
    deleteNotification,
    refresh,
    loadSettings,
  };
}
